import React from 'react';
import AgeSelection from './AgeSelection';
import SocialProof from './SocialProof';
import WalkingExperience from './WalkingExperience';
import WalkingBenefits from './WalkingBenefits';
import GoalSelection from './GoalSelection';
import { useOnboardingStore, PlanType } from '../../store/onboardingStore';

const SolutionOverview = React.lazy(() => import('./SolutionOverview'));
const AdditionalGoals = React.lazy(() => import('./AdditionalGoals'));
const BodyBuildSelection = React.lazy(() => import('./BodyBuildSelection'));
const DreamBodySelection = React.lazy(() => import('./DreamBodySelection'));
const TargetZones = React.lazy(() => import('./TargetZones'));
const WeightChange = React.lazy(() => import('./WeightChange'));
const BestShape = React.lazy(() => import('./BestShape'));
const StepByStep = React.lazy(() => import('./StepByStep'));
const ExerciseFrequency = React.lazy(() => import('./ExerciseFrequency'));
const WalkFrequency = React.lazy(() => import('./WalkFrequency'));
const BreathStairs = React.lazy(() => import('./BreathStairs'));
const Struggles = React.lazy(() => import('./Struggles'));
const WalkingConcern = React.lazy(() => import('./WalkingConcern'));
const AchieveGoal = React.lazy(() => import('./AchieveGoal'));
const HealthyChoices = React.lazy(() => import('./HealthyChoices'));
const LoseMotivation = React.lazy(() => import('./LoseMotivation'));
const NeedReminders = React.lazy(() => import('./NeedReminders'));
const WorkSchedule = React.lazy(() => import('./WorkSchedule'));
const TypicalDay = React.lazy(() => import('./TypicalDay'));
const EnergyLevels = React.lazy(() => import('./EnergyLevels'));
const StressLevel = React.lazy(() => import('./StressLevel'));
const SleepDuration = React.lazy(() => import('./SleepDuration'));
const SleepIssues = React.lazy(() => import('./SleepIssues'));
const SleepBenefits = React.lazy(() => import('./SleepBenefits'));
const WaterIntake = React.lazy(() => import('./WaterIntake'));
const EatingHabits = React.lazy(() => import('./EatingHabits'));
const FoodCravings = React.lazy(() => import('./FoodCravings'));
const DietPreference = React.lazy(() => import('./DietPreference'));
const MealPlanBenefits = React.lazy(() => import('./MealPlanBenefits'));
const FitnessMotivation = React.lazy(() => import('./FitnessMotivation'));
const WeightGainFactors = React.lazy(() => import('./WeightGainFactors'));
const HeightCollection = React.lazy(() => import('./HeightCollection'));
const WeightCollection = React.lazy(() => import('./WeightCollection'));
const GoalWeightCollection = React.lazy(() => import('./GoalWeightCollection'));
const AgeInput = React.lazy(() => import('./AgeInput'));
const WellnessProfile = React.lazy(() => import('./WellnessProfile'));
const ImportantEvent = React.lazy(() => import('./ImportantEvent'));
const EventDate = React.lazy(() => import('./EventDate'));
const GoalPrediction = React.lazy(() => import('./GoalPrediction'));
const PlanGenerating = React.lazy(() => import('./PlanGenerating'));
const EmailCollectionPage = React.lazy(() => import('./EmailCollection'));
const NameCollection = React.lazy(() => import('./NameCollection'));
const PlanReady = React.lazy(() => import('./PlanReady'));
const DiscountPromo = React.lazy(() => import('./DiscountPromo'));
const PaywallPage = React.lazy(() => import('./Paywall'));
const Step1CreateAccount = React.lazy(() => import('./Step1CreateAccount'));
const Step2AddOffer = React.lazy(() => import('./Step2AddOffer'));
const Step2Alternative = React.lazy(() => import('./Step2Alternative'));
const Step3GetApp = React.lazy(() => import('./Step3GetApp'));
const PaymentPageWrapper = React.lazy(() => import('./PaymentPageWrapper'));

const OnboardingFlow: React.FC = () => {
    const {
        currentStep,
        userData,
        backFromPayment,
        setCurrentStep: storeSetCurrentStep,
        setEmail,
        setPlan,
        setAccountInfo,
        setOffer,
        setBackFromPayment,
        setUserData,
        setFunnelData
    } = useOnboardingStore();

    // Local display state with transition keeps the old step visible
    // while the new lazy component loads (prevents header flash)
    const [displayStep, setDisplayStep] = React.useState(currentStep);
    React.useEffect(() => {
        React.startTransition(() => {
            setDisplayStep(currentStep);
        });
    }, [currentStep]);

    const setCurrentStep = storeSetCurrentStep;

    const handleAgeSelected = (ageGroup: string) => {
        setFunnelData({ ageGroup });
        setCurrentStep('socialproof');
    };

    const handleSocialProofContinue = () => {
        setCurrentStep('walkingexperience');
    };

    const handleSocialProofBack = () => {
        setCurrentStep('age');
    };

    const handleWalkingExperienceSelect = (hasExperience: boolean) => {
        setFunnelData({ hasWalkingExperience: hasExperience });
        setCurrentStep('walkingbenefits');
    };

    const handleWalkingExperienceBack = () => {
        setCurrentStep('socialproof');
    };

    const handleWalkingBenefitsContinue = () => {
        setCurrentStep('goalselection');
    };

    const handleWalkingBenefitsBack = () => {
        setCurrentStep('walkingexperience');
    };

    const handleGoalSelect = (goal: string) => {
        setFunnelData({ goal });
        setCurrentStep('solutionoverview');
    };

    const handleGoalBack = () => {
        setCurrentStep('walkingbenefits');
    };

    const handleSolutionContinue = () => {
        setCurrentStep('additionalgoals');
    };

    const handleSolutionBack = () => {
        setCurrentStep('goalselection');
    };

    const handleAdditionalGoalsContinue = (goals: string[]) => {
        setFunnelData({ additionalGoals: goals });
        setCurrentStep('bodybuild');
    };

    const handleAdditionalGoalsBack = () => {
        setCurrentStep('solutionoverview');
    };

    const handleBodyBuildSelect = (bodyBuild: string) => {
        setFunnelData({ bodyBuild });
        setCurrentStep('dreambody');
    };

    const handleBodyBuildBack = () => {
        setCurrentStep('additionalgoals');
    };

    const handleDreamBodySelect = (dreamBody: string) => {
        setFunnelData({ dreamBody });
        setCurrentStep('targetzones');
    };

    const handleDreamBodyBack = () => {
        setCurrentStep('bodybuild');
    };

    const handleTargetZoneSelect = (zone: string) => {
        setFunnelData({ targetZone: zone });
        setCurrentStep('weightchange');
    };

    const handleTargetZonesBack = () => {
        setCurrentStep('dreambody');
    };

    const handleWeightChangeSelect = (weightChange: string) => {
        setFunnelData({ weightChange });
        setCurrentStep('bestshape');
    };

    const handleWeightChangeBack = () => {
        setCurrentStep('targetzones');
    };

    const handleBestShapeSelect = (bestShape: string) => {
        setFunnelData({ bestShape });
        setCurrentStep('stepbystep');
    };

    const handleBestShapeBack = () => {
        setCurrentStep('weightchange');
    };

    const handleStepByStepContinue = () => {
        setCurrentStep('exercisefrequency');
    };

    const handleStepByStepBack = () => {
        setCurrentStep('bestshape');
    };

    const handleExerciseFrequencySelect = (frequency: string) => {
        setFunnelData({ exerciseFrequency: frequency });
        setCurrentStep('walkfrequency');
    };

    const handleExerciseFrequencyBack = () => {
        setCurrentStep('stepbystep');
    };

    const handleWalkFrequencySelect = (frequency: string) => {
        setFunnelData({ walkFrequency: frequency });
        setCurrentStep('breathstairs');
    };

    const handleWalkFrequencyBack = () => {
        setCurrentStep('exercisefrequency');
    };

    const handleBreathStairsSelect = (breathLevel: string) => {
        setFunnelData({ breathStairs: breathLevel });
        setCurrentStep('struggles');
    };

    const handleBreathStairsBack = () => {
        setCurrentStep('walkfrequency');
    };

    const handleStrugglesContinue = (struggles: string[]) => {
        setFunnelData({ struggles });
        setCurrentStep('walkingconcern');
    };

    const handleStrugglesBack = () => {
        setCurrentStep('breathstairs');
    };

    const handleWalkingConcernSelect = (concern: string) => {
        setFunnelData({ walkingConcern: concern });
        setCurrentStep('achievegoal');
    };

    const handleWalkingConcernBack = () => {
        setCurrentStep('struggles');
    };

    const handleAchieveGoalContinue = () => {
        setCurrentStep('healthychoices');
    };

    const handleAchieveGoalBack = () => {
        setCurrentStep('walkingconcern');
    };

    const handleHealthyChoicesSelect = (rating: number) => {
        setFunnelData({ healthyChoicesRating: rating });
        setCurrentStep('losemotivation');
    };

    const handleHealthyChoicesBack = () => {
        setCurrentStep('achievegoal');
    };

    const handleLoseMotivationSelect = (rating: number) => {
        setFunnelData({ loseMotivationRating: rating });
        setCurrentStep('needreminders');
    };

    const handleLoseMotivationBack = () => {
        setCurrentStep('healthychoices');
    };

    const handleNeedRemindersSelect = (rating: number) => {
        setFunnelData({ needRemindersRating: rating });
        setCurrentStep('workschedule');
    };

    const handleNeedRemindersBack = () => {
        setCurrentStep('losemotivation');
    };

    const handleWorkScheduleSelect = (schedule: string) => {
        setFunnelData({ workSchedule: schedule });
        setCurrentStep('typicalday');
    };

    const handleWorkScheduleBack = () => {
        setCurrentStep('needreminders');
    };

    const handleTypicalDaySelect = (activity: string) => {
        setFunnelData({ typicalDay: activity });
        setCurrentStep('energylevels');
    };

    const handleTypicalDayBack = () => {
        setCurrentStep('workschedule');
    };

    const handleEnergyLevelsSelect = (level: string) => {
        setFunnelData({ energyLevels: level });
        setCurrentStep('stresslevel');
    };

    const handleEnergyLevelsBack = () => {
        setCurrentStep('typicalday');
    };

    const handleStressLevelSelect = (level: string) => {
        setFunnelData({ stressLevel: level });
        setCurrentStep('sleepduration');
    };

    const handleStressLevelBack = () => {
        setCurrentStep('energylevels');
    };

    const handleSleepDurationSelect = (duration: string) => {
        setFunnelData({ sleepDuration: duration });
        setCurrentStep('sleepissues');
    };

    const handleSleepDurationBack = () => {
        setCurrentStep('stresslevel');
    };

    const handleSleepIssuesContinue = (issues: string[]) => {
        setFunnelData({ sleepIssues: issues });
        setCurrentStep('sleepbenefits');
    };

    const handleSleepIssuesBack = () => {
        setCurrentStep('sleepduration');
    };

    const handleSleepBenefitsContinue = () => {
        setCurrentStep('waterintake');
    };

    const handleSleepBenefitsBack = () => {
        setCurrentStep('sleepissues');
    };

    const handleWaterIntakeSelect = (intake: string) => {
        setFunnelData({ waterIntake: intake });
        setCurrentStep('eatinghabits');
    };

    const handleWaterIntakeBack = () => {
        setCurrentStep('sleepbenefits');
    };

    const handleEatingHabitsContinue = (habits: string[]) => {
        setFunnelData({ eatingHabits: habits });
        setCurrentStep('foodcravings');
    };

    const handleEatingHabitsBack = () => {
        setCurrentStep('waterintake');
    };

    const handleFoodCravingsContinue = (cravings: string[]) => {
        setFunnelData({ foodCravings: cravings });
        setCurrentStep('dietpreference');
    };

    const handleFoodCravingsBack = () => {
        setCurrentStep('eatinghabits');
    };

    const handleDietPreferenceSelect = (diet: string) => {
        setFunnelData({ dietPreference: diet });
        setCurrentStep('mealplanbenefits');
    };

    const handleDietPreferenceBack = () => {
        setCurrentStep('foodcravings');
    };

    const handleMealPlanBenefitsContinue = () => {
        setCurrentStep('fitnessmotivation');
    };

    const handleMealPlanBenefitsBack = () => {
        setCurrentStep('dietpreference');
    };

    const handleFitnessMotivationSelect = (reason: string) => {
        setFunnelData({ fitnessMotivation: reason });
        setCurrentStep('weightgainfactors');
    };

    const handleFitnessMotivationBack = () => {
        setCurrentStep('mealplanbenefits');
    };

    const handleWeightGainFactorsContinue = (factors: string[]) => {
        setFunnelData({ weightGainFactors: factors });
        setCurrentStep('height');
    };

    const handleWeightGainFactorsBack = () => {
        setCurrentStep('fitnessmotivation');
    };

    const handleHeightContinue = (height: number, unit: 'cm' | 'ft') => {
        setFunnelData({ height, heightUnit: unit });
        setCurrentStep('weight');
    };

    const handleHeightBack = () => {
        setCurrentStep('weightgainfactors');
    };

    const handleWeightContinue = (weight: number, unit: 'kg' | 'lbs') => {
        setFunnelData({ weight, weightUnit: unit });
        setCurrentStep('goalweight');
    };

    const handleWeightBack = () => {
        setCurrentStep('height');
    };

    const handleGoalWeightContinue = (goalWeight: number, unit: 'kg' | 'lbs') => {
        setFunnelData({ goalWeight, goalWeightUnit: unit });
        setCurrentStep('ageinput');
    };

    const handleGoalWeightBack = () => {
        setCurrentStep('weight');
    };

    const handleAgeInputContinue = (age: number) => {
        setFunnelData({ age });
        setCurrentStep('wellnessprofile');
    };

    const handleAgeInputBack = () => {
        setCurrentStep('goalweight');
    };

    const handleWellnessProfileContinue = () => {
        setCurrentStep('importantevent');
    };

    const handleWellnessProfileBack = () => {
        setCurrentStep('ageinput');
    };

    const handleImportantEventSelect = (event: string) => {
        setFunnelData({ importantEvent: event });
        setCurrentStep('eventdate');
    };

    const handleImportantEventBack = () => {
        setCurrentStep('wellnessprofile');
    };

    const handleEventDateContinue = (date: string) => {
        setFunnelData({ eventDate: date });
        setCurrentStep('goalprediction');
    };

    const handleEventDateSkip = () => {
        setCurrentStep('goalprediction');
    };

    const handleEventDateBack = () => {
        setCurrentStep('importantevent');
    };

    const handleGoalPredictionContinue = () => {
        setCurrentStep('plangenerating');
    };

    const handleGoalPredictionBack = () => {
        setCurrentStep('eventdate');
    };

    const handlePlanGeneratingComplete = () => {
        setCurrentStep('email');
    };

    const handleEmailCollected = (email: string, referralCode?: string) => {
        setEmail(email);
        setUserData({ promoCode: referralCode });
        setCurrentStep('name');
    };

    const handleLoadingComplete = () => {
        setCurrentStep('name');
    };

    const handleNameContinue = (name: string) => {
        setUserData({ firstName: name });
        setCurrentStep('planready');
    };

    const handleNameBack = () => {
        setCurrentStep('email');
    };

    const handlePlanReadyContinue = () => {
        setCurrentStep('discount');
    };

    const handlePlanReadyBack = () => {
        setCurrentStep('name');
    };

    const handleDiscountComplete = () => {
        setCurrentStep('paywall');
    };

    // UPDATED: Now accepts all plan types (monthly, yearly, 1_week_trial, 4_week, 12_week)
    const handlePlanSelected = (planType: PlanType, planId: string | null) => {
        setPlan(planType, planId);
        setCurrentStep('payment');
    };

    const handlePaymentComplete = () => {
        setCurrentStep('1');
    };

    const handlePaymentBack = () => {
        setBackFromPayment(true);
        setCurrentStep('paywall');
    };

    const handleStep1Complete = (data: {
        fullName: string;
        email: string;
        password: string;
    }) => {
        setAccountInfo(data.fullName, data.email, data.password);
        setCurrentStep('2-1');
    };

    const handleStep21Complete = () => {
        setCurrentStep('2-2');
    };

    const handleStep22Complete = (selectedOffer: string | null) => {
        setOffer(selectedOffer);
        setCurrentStep('3');
    };

    const handleOnboardingComplete = () => {
        const downloadUrl = process.env.REACT_APP_DOWNLOAD_APP_URL || '';
        console.log(downloadUrl)
        window.open(downloadUrl, '_blank');
    };

    return (
        <React.Suspense fallback={null}>
            {displayStep === 'age' && (
                <AgeSelection onSelect={handleAgeSelected} />
            )}
            {displayStep === 'socialproof' && (
                <SocialProof
                    ageGroup={userData.funnelData?.ageGroup || '50-59'}
                    onContinue={handleSocialProofContinue}
                    onBack={handleSocialProofBack}
                />
            )}
            {displayStep === 'walkingexperience' && (
                <WalkingExperience
                    onSelect={handleWalkingExperienceSelect}
                    onBack={handleWalkingExperienceBack}
                />
            )}
            {displayStep === 'walkingbenefits' && (
                <WalkingBenefits
                    onContinue={handleWalkingBenefitsContinue}
                    onBack={handleWalkingBenefitsBack}
                    hasWalkingExperience={userData.funnelData?.hasWalkingExperience ?? false}
                />
            )}
            {displayStep === 'goalselection' && (
                <GoalSelection
                    onSelect={handleGoalSelect}
                    onBack={handleGoalBack}
                />
            )}
            {displayStep === 'solutionoverview' && (
                <SolutionOverview
                    onContinue={handleSolutionContinue}
                    onBack={handleSolutionBack}
                />
            )}
            {displayStep === 'additionalgoals' && (
                <AdditionalGoals
                    onContinue={handleAdditionalGoalsContinue}
                    onBack={handleAdditionalGoalsBack}
                />
            )}
            {displayStep === 'bodybuild' && (
                <BodyBuildSelection
                    onSelect={handleBodyBuildSelect}
                    onBack={handleBodyBuildBack}
                />
            )}
            {displayStep === 'dreambody' && (
                <DreamBodySelection
                    onSelect={handleDreamBodySelect}
                    onBack={handleDreamBodyBack}
                />
            )}
            {displayStep === 'targetzones' && (
                <TargetZones
                    onSelect={handleTargetZoneSelect}
                    onBack={handleTargetZonesBack}
                />
            )}
            {displayStep === 'weightchange' && (
                <WeightChange
                    onSelect={handleWeightChangeSelect}
                    onBack={handleWeightChangeBack}
                />
            )}
            {displayStep === 'bestshape' && (
                <BestShape
                    onSelect={handleBestShapeSelect}
                    onBack={handleBestShapeBack}
                />
            )}
            {displayStep === 'stepbystep' && (
                <StepByStep
                    onContinue={handleStepByStepContinue}
                    onBack={handleStepByStepBack}
                />
            )}
            {displayStep === 'exercisefrequency' && (
                <ExerciseFrequency
                    onSelect={handleExerciseFrequencySelect}
                    onBack={handleExerciseFrequencyBack}
                />
            )}
            {displayStep === 'walkfrequency' && (
                <WalkFrequency
                    onSelect={handleWalkFrequencySelect}
                    onBack={handleWalkFrequencyBack}
                />
            )}
            {displayStep === 'breathstairs' && (
                <BreathStairs
                    onSelect={handleBreathStairsSelect}
                    onBack={handleBreathStairsBack}
                />
            )}
            {displayStep === 'struggles' && (
                <Struggles
                    onContinue={handleStrugglesContinue}
                    onBack={handleStrugglesBack}
                />
            )}
            {displayStep === 'walkingconcern' && (
                <WalkingConcern
                    onSelect={handleWalkingConcernSelect}
                    onBack={handleWalkingConcernBack}
                />
            )}
            {displayStep === 'achievegoal' && (
                <AchieveGoal
                    onContinue={handleAchieveGoalContinue}
                    onBack={handleAchieveGoalBack}
                />
            )}
            {displayStep === 'healthychoices' && (
                <HealthyChoices
                    onSelect={handleHealthyChoicesSelect}
                    onBack={handleHealthyChoicesBack}
                />
            )}
            {displayStep === 'losemotivation' && (
                <LoseMotivation
                    onSelect={handleLoseMotivationSelect}
                    onBack={handleLoseMotivationBack}
                />
            )}
            {displayStep === 'needreminders' && (
                <NeedReminders
                    onSelect={handleNeedRemindersSelect}
                    onBack={handleNeedRemindersBack}
                />
            )}
            {displayStep === 'workschedule' && (
                <WorkSchedule
                    onSelect={handleWorkScheduleSelect}
                    onBack={handleWorkScheduleBack}
                />
            )}
            {displayStep === 'typicalday' && (
                <TypicalDay
                    onSelect={handleTypicalDaySelect}
                    onBack={handleTypicalDayBack}
                />
            )}
            {displayStep === 'energylevels' && (
                <EnergyLevels
                    onSelect={handleEnergyLevelsSelect}
                    onBack={handleEnergyLevelsBack}
                />
            )}
            {displayStep === 'stresslevel' && (
                <StressLevel
                    onSelect={handleStressLevelSelect}
                    onBack={handleStressLevelBack}
                />
            )}
            {displayStep === 'sleepduration' && (
                <SleepDuration
                    onSelect={handleSleepDurationSelect}
                    onBack={handleSleepDurationBack}
                />
            )}
            {displayStep === 'sleepissues' && (
                <SleepIssues
                    onContinue={handleSleepIssuesContinue}
                    onBack={handleSleepIssuesBack}
                />
            )}
            {displayStep === 'sleepbenefits' && (
                <SleepBenefits
                    onContinue={handleSleepBenefitsContinue}
                    onBack={handleSleepBenefitsBack}
                />
            )}
            {displayStep === 'waterintake' && (
                <WaterIntake
                    onSelect={handleWaterIntakeSelect}
                    onBack={handleWaterIntakeBack}
                />
            )}
            {displayStep === 'eatinghabits' && (
                <EatingHabits
                    onContinue={handleEatingHabitsContinue}
                    onBack={handleEatingHabitsBack}
                />
            )}
            {displayStep === 'foodcravings' && (
                <FoodCravings
                    onContinue={handleFoodCravingsContinue}
                    onBack={handleFoodCravingsBack}
                />
            )}
            {displayStep === 'dietpreference' && (
                <DietPreference
                    onSelect={handleDietPreferenceSelect}
                    onBack={handleDietPreferenceBack}
                />
            )}
            {displayStep === 'mealplanbenefits' && (
                <MealPlanBenefits
                    onContinue={handleMealPlanBenefitsContinue}
                    onBack={handleMealPlanBenefitsBack}
                />
            )}
            {displayStep === 'fitnessmotivation' && (
                <FitnessMotivation
                    onSelect={handleFitnessMotivationSelect}
                    onBack={handleFitnessMotivationBack}
                />
            )}
            {displayStep === 'weightgainfactors' && (
                <WeightGainFactors
                    onContinue={handleWeightGainFactorsContinue}
                    onBack={handleWeightGainFactorsBack}
                />
            )}
            {displayStep === 'height' && (
                <HeightCollection
                    onContinue={handleHeightContinue}
                    onBack={handleHeightBack}
                />
            )}
            {displayStep === 'weight' && (
                <WeightCollection
                    heightValue={userData.funnelData?.height || 170}
                    heightUnit={userData.funnelData?.heightUnit || 'cm'}
                    onContinue={handleWeightContinue}
                    onBack={handleWeightBack}
                />
            )}
            {displayStep === 'goalweight' && (
                <GoalWeightCollection
                    currentWeight={userData.funnelData?.weight || 70}
                    currentWeightUnit={userData.funnelData?.weightUnit || 'kg'}
                    onContinue={handleGoalWeightContinue}
                    onBack={handleGoalWeightBack}
                />
            )}
            {displayStep === 'ageinput' && (
                <AgeInput
                    onContinue={handleAgeInputContinue}
                    onBack={handleAgeInputBack}
                />
            )}
            {displayStep === 'wellnessprofile' && (
                <WellnessProfile
                    heightValue={userData.funnelData?.height || 170}
                    heightUnit={userData.funnelData?.heightUnit || 'cm'}
                    weightValue={userData.funnelData?.weight || 70}
                    weightUnit={userData.funnelData?.weightUnit || 'kg'}
                    typicalDay={userData.funnelData?.typicalDay}
                    walkFrequency={userData.funnelData?.walkFrequency}
                    onContinue={handleWellnessProfileContinue}
                    onBack={handleWellnessProfileBack}
                />
            )}
            {displayStep === 'importantevent' && (
                <ImportantEvent
                    onSelect={handleImportantEventSelect}
                    onBack={handleImportantEventBack}
                />
            )}
            {displayStep === 'eventdate' && (
                <EventDate
                    onContinue={handleEventDateContinue}
                    onSkip={handleEventDateSkip}
                    onBack={handleEventDateBack}
                />
            )}
            {displayStep === 'goalprediction' && (
                <GoalPrediction
                    currentWeight={userData.funnelData?.weight || 70}
                    goalWeight={userData.funnelData?.goalWeight || 65}
                    weightUnit={userData.funnelData?.weightUnit || 'kg'}
                    eventName={userData.funnelData?.importantEvent}
                    eventDate={userData.funnelData?.eventDate}
                    onContinue={handleGoalPredictionContinue}
                    onBack={handleGoalPredictionBack}
                />
            )}
            {displayStep === 'plangenerating' && (
                <PlanGenerating onComplete={handlePlanGeneratingComplete} />
            )}
            {displayStep === 'email' && (
                <EmailCollectionPage onContinue={handleEmailCollected} />
            )}
            {displayStep === 'name' && (
                <NameCollection
                    onContinue={handleNameContinue}
                    onBack={handleNameBack}
                    userData={userData}
                />
            )}
            {displayStep === 'planready' && (
                <PlanReady
                    name={userData.firstName || ''}
                    onContinue={handlePlanReadyContinue}
                    onBack={handlePlanReadyBack}
                />
            )}
            {displayStep === 'discount' && (
                <DiscountPromo
                    onComplete={handleDiscountComplete}
                    userName={userData.firstName || ''}
                />
            )}
            {displayStep === 'paywall' && (
                <PaywallPage onSelectPlan={handlePlanSelected} showFinalPromo={backFromPayment} />
            )}
            {displayStep === 'payment' && userData.selectedPlan && (
                <PaymentPageWrapper
                    selectedSubscriptionPlanId={userData.selectedPlanId || undefined}
                    planType={userData.selectedPlan}
                    promoCode={userData.promoCode || ''}
                    onPaymentComplete={handlePaymentComplete}
                    onBack={handlePaymentBack}
                />
            )}
            {displayStep === '1' && (
                <Step1CreateAccount
                    email={userData.email}
                    firstName={userData.firstName}
                    funnelData={{
                        height: userData.funnelData?.height,
                        heightUnit: userData.funnelData?.heightUnit,
                        weight: userData.funnelData?.weight,
                        weightUnit: userData.funnelData?.weightUnit,
                        goalWeight: userData.funnelData?.goalWeight,
                        age: userData.funnelData?.age
                    }}
                    onNext={handleStep1Complete}
                />
            )}
            {displayStep === '2-1' && (
                <Step2Alternative onNext={handleStep21Complete} />
            )}
            {displayStep === '2-2' && (
                <Step2AddOffer onNext={handleStep22Complete} />
            )}
            {displayStep === '3' && (
                <Step3GetApp
                    email={userData.email}
                    onComplete={handleOnboardingComplete}
                />
            )}
        </React.Suspense>
    );
};

export default OnboardingFlow;